import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ActivityAssignment } from './activity-assignment';
export { ActivityAssignment };
import { activityAssignmentSchema } from '@schemas/business-objects/activity-assignment';
import { SlotFormGroup } from './slot-form-group';
import { TermTutorFormGroup } from './term-tutor-form-group';
import type { Id } from '../base-types';
import type { Term } from './term';
import type { TermTime } from './term-time';
import type { Tutor } from './tutor';
import type { User } from './user';

export class ActivityAssignmentFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    from: SchemaFormControl;
    to: SchemaFormControl;
    status: SchemaFormControl;
    termTutorId: SchemaFormControl;
    termTutor: TermTutorFormGroup;
    slotId: SchemaFormControl;
    slot: SlotFormGroup;
    replacesId: SchemaFormControl;
    replaces: ActivityAssignmentFormGroup;
    replacedBy: ActivityAssignmentFormGroup;
  };

  declare value: Partial<ActivityAssignment>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(activityAssignmentSchema, ActivityAssignment, data, options);
  }

  get object(): ActivityAssignment {
    return new ActivityAssignment(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'termTutor':
        return new TermTutorFormGroup();
      case 'slot':
        return new SlotFormGroup();
      case 'replaces':
        return new ActivityAssignmentFormGroup();
      case 'replacedBy':
        return new ActivityAssignmentFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ActivityAssignment[]> {
    return ActivityAssignment.find(filter);
  }

  static async findOne(filter?: any): Promise<ActivityAssignment | null> {
    return ActivityAssignment.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<ActivityAssignment> {
    return ActivityAssignment.findById(id, filter);
  }

  static async create(
    data: Partial<ActivityAssignment>,
  ): Promise<ActivityAssignment> {
    return ActivityAssignment.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<ActivityAssignment>,
  ): Promise<ActivityAssignment> {
    return ActivityAssignment.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return ActivityAssignment.deleteById(id);
  }

  static async checkExistingProposal(
    termTutorId: Id,
    slotId: Id | undefined,
    name: string,
    replacesId: Id | undefined,
  ): Promise<any> {
    return ActivityAssignment.checkExistingProposal(
      termTutorId,
      slotId,
      name,
      replacesId,
    );
  }
}

export class ActivityAssignmentFormArray extends PersistedFormArray {
  readonly entityName = 'ActivityAssignment' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(activityAssignmentSchema, ActivityAssignment, options);
  }

  protected override createItemGroup() {
    return new ActivityAssignmentFormGroup();
  }

  get array(): ActivityAssignment[] {
    return this.controls.map(
      (group: ActivityAssignmentFormGroup) => group.object,
    );
  }
}

export class ActivityAssignmentPersistedArray extends PersistedArray<ActivityAssignment> {
  readonly entityName = 'ActivityAssignment' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ActivityAssignment, options);
  }
}
